import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import { LeadCard } from "../LeadCard";

interface LeadStageColumnProps {
  stage: {
    value: string;
    label: string;
  };
  leads: any[];
  isCollapsed: boolean;
  onToggleCollapse: (stage: string) => void;
  onUpdate: () => Promise<any>;
  isMobile?: boolean;
}

export const LeadStageColumn = ({
  stage,
  leads,
  isCollapsed,
  onToggleCollapse,
  onUpdate,
  isMobile = false,
}: LeadStageColumnProps) => {
  return (
    <Collapsible
      key={stage.value}
      open={!isCollapsed}
      className={isMobile ? "w-full" : "w-[350px] shrink-0"}
    >
      <div className="bg-muted/20 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm"
                className="p-0 h-6 w-6"
                onClick={() => onToggleCollapse(stage.value)}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <h3 className="font-semibold">{stage.label}</h3>
            <Badge variant="secondary">
              {leads.length}
            </Badge>
          </div>
        </div>

        <CollapsibleContent>
          <Droppable droppableId={stage.value}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`space-y-4 rounded-lg min-h-[200px] transition-colors ${
                  snapshot.isDraggingOver ? 'bg-muted/50' : ''
                }`}
              >
                {leads.map((lead, index) => (
                  <Draggable key={lead.id} draggableId={lead.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`${snapshot.isDragging ? 'opacity-50' : ''}`}
                      >
                        <LeadCard lead={lead} refetch={onUpdate} />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};